import { Divider, Grid, makeStyles, Typography } from '@material-ui/core';
import React, { useRef } from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import InputAdornmentChip from 'components/InputAdornmentChip';
import { Formik } from 'formik';
import i18n from 'i18n';
import Button from 'components/Button';
import {
  EnvironmentFields,
  FormStandardImageProps,
  ServerFields,
} from 'modules/environments/interfaces';
import { publishNewImageRequest } from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import Permission from 'components/ValidPermission';
import { useParams } from 'react-router';
import { ModalFields } from 'modules/modal/interfaces';
import ImagesLogs from 'containers/Environments/ListServers/PreparationServer/ImagesLogs';
import PageTitle from 'components/PageTitle';
import styles from './styles';
import schema from './schema';

const useStyles = makeStyles(styles);

interface Props {
  environmentSelected: EnvironmentFields | null;
  preparationServer: ServerFields;
  highlightSection?: string;
  publishNewImageRequest: ({
    environmentUuid,
    instanceId,
    values,
    type,
  }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
}

const DefaultPublication = ({
  environmentSelected,
  preparationServer,
  highlightSection,
  publishNewImageRequest,
  modalOpen,
  modalClose,
  setCurrentTab,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();

  const initialValues: FormStandardImageProps = {
    notification_email: [],
  };

  const sectionRef = useRef<HTMLDivElement>(null);

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };
  if (highlightSection === 'logs') {
    scrollToSection();
  }

  const handleConfirmPublish = values => {
    modalOpen({
      title: i18n.t('DEFAULT_PUBLICATION_KEY'),
      maxWidth: 'sm',
      footer: (
        <Grid>
          <Button
            id="submit-button"
            submitButton
            onClick={() => {
              publishNewImageRequest({
                environmentUuid: id,
                instanceId: preparationServer?.instance_id,
                values,
                type: 'default',
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
      content: (
        <Typography className={classes.headerText}>
          {i18n.t('DEFAULT_PUBLICATION_CONFIRM_KEY')}
        </Typography>
      ),
    });
  };

  return (
    <>
      <Grid container className={classes.formGrid}>
        <Grid container className={classes.header}>
          <Typography className={classes.headerText}>
            {i18n.t('ENVIRONMENTS_PAGE.KEEP_USER_LOG_MSG_KEY')}
          </Typography>
        </Grid>
        <Grid container>
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={handleConfirmPublish}
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ setFieldValue, submitForm }) => (
              <Grid container spacing={2}>
                <InputAdornmentChip
                  gridXs={12}
                  label={i18n.t(
                    'ENVIRONMENTS_PAGE.EMAIL_NOTIFICATION_IMAGE_KEY'
                  )}
                  stateKey="notification_email"
                  type="email"
                  values={environmentSelected?.trusted_users?.split(',') || []}
                  setFieldValue={setFieldValue}
                />
                <Grid container justify="flex-end" className={classes.buttons}>
                  <Button id="submit-button" onClick={submitForm} submitButton>
                    {i18n.t('ENVIRONMENTS_PAGE.PUBLICATIONS_KEY')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      </Grid>
      <Grid container>
        <Divider className={classes.divider} />
      </Grid>
      <Grid container>
        <Grid container className={classes.actionsGrid}>
          <Grid item>
            <PageTitle
              title={i18n.t('ENVIRONMENTS_PAGE.LOGS_PUBLICATION_KEY')}
              subtitle={i18n.t('LOGS_PUBLICATION_SUBSCRIPTION_KEY')}
            />
          </Grid>
          <Grid item>
            <Permission value="list_models">
              <Button
                className={classes.seeModelsButtons}
                variant="outlined"
                onClick={() => setCurrentTab(2)}
              >
                {i18n.t('SEE_GENERATED_MODELS_KEY')}
              </Button>
            </Permission>
          </Grid>
        </Grid>
        <Grid container innerRef={sectionRef}>
          <ImagesLogs id={id} instanceId={preparationServer?.instance_id} />
        </Grid>
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected },
}: ApplicationState) => ({
  environmentSelected,
});

export default connect(mapStateToProps, {
  publishNewImageRequest,
  modalOpen,
  modalClose,
})(DefaultPublication);
